<template>
  <v-row>
    <v-col cols="7" v-if="isFetching">
      <main-loader></main-loader>
    </v-col>
    <v-col cols="7" v-else>
      <v-row no-gutters class="pb-5" justify="space-between">
        <v-col cols="auto">
          <base-button
            :disabled="$attrs.disabled"
            @click="openAddDialog"
            translation-path="button.addDictionary"
            class="mr-0"
          ></base-button>
        </v-col>
        <v-col cols="5">
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            class="searchInput"
          >
          </data-input>
        </v-col>
      </v-row>
      <v-row
        no-gutters
        class="border align-center element justify-space-between flex-nowrap px-4"
        style="background-color: white"
        v-for="dictionary in searchedItems"
        :key="dictionary.id"
      >
        <v-col cols="10">
          <div
            class="text-left text-truncate text-body-2 flex-shrink-0 py-2"
            :class="$attrs.disabled ? 'text--disabled' : 'text--black'"
          >
            {{ dictionary.dict_value }}
          </div>
        </v-col>
        <v-col cols="auto">
          <dots-menu
            :disabled="$attrs.disabled"
            font-size="19px"
            :items="dotsItems"
            @edit="openEditDialog(dictionary)"
            @delete="openDeleteDialog(dictionary)"
          >
          </dots-menu>
        </v-col>
      </v-row>
    </v-col>

    <main-input-dialog
      v-if="isDialogLoaded"
      :inputs="dialogInputs"
      :is-visible.sync="isDialogVisible"
      :loading="isConfirmButtonLoading"
      :title-translation="$i18n.t('dialog.title.addDictionaryValue')"
      @agree="dialogAgreeAction"
      agree-text="dialog.agreeSave"
      @modalClosed="resetData"
      header-shadow
      max-height="60vh"
      body-height="25vh"
    />
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteDictionaryValue')"
      :body="$i18n.t('dialog.body.deleteDictionaryConfirmation')"
      @agree="handleDictionaryDelete"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
  </v-row>
</template>
<script>
import MainLoader from '@/components/MainLoader';
import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';
import { call, get } from 'vuex-pathify';

export default {
  name: 'ZDMFailureStatusDictionaryList',
  components: {
    MainLoader,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
    DataInput,
  },
  data: () => ({
    search: '',
    isFetching: false,
    isDialogVisible: false,
    isDialogLoaded: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isConfirmButtonLoading: false,
    dotsItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
      },
    ],
    editDictionary: undefined,
  }),
  computed: {
    dictionaries: get('zdm/failureStatusDescriptions'),
    searchedItems() {
      if (!this.search) {
        return this.dictionaries;
      }
      return this.dictionaries.filter(a => a.dict_value.toLowerCase().includes(this.search.toLowerCase()));
    },
    dialogInputs() {
      return [
        {
          name: 'dict_value',
          rules: [v => !!v || 'rules.required'],
          translationPath: 'dialog.body.valueName',
          cols: '12',
          dataType: {
            name: 'text',
          },
          clearable: true,
          align: 'start',
          value: this.editDictionary?.dict_value,
        },
        {
          name: 'description',
          rules: [v => !!v || 'rules.required'],
          translationPath: 'dialog.body.failureDescription',
          cols: '12',
          clearable: true,
          align: 'start',
          dataType: {
            name: 'textarea',
          },
          rows: 1,
          autoGrow: true,
          value: this.editDictionary?.description,
        },
      ];
    },
    dialogAgreeAction() {
      return this.editDictionary === undefined ? this.addDictionary : this.saveDictionary;
    },
  },
  methods: {
    getDescriptions: call('zdm/getStatusDescriptions'),
    addDescription: call('zdm/addStatusDescription'),
    editDescription: call('zdm/editStatusDescription'),
    deleteDescription: call('zdm/deleteStatusDescription'),

    async saveDictionary() {
      const body = {
        dict_value: this.dialogInputs[0].value,
        description: this.dialogInputs[1].value,
      };
      this.isConfirmButtonLoading = true;
      try {
        await this.editDescription({ id: this.editDictionary.id, body: body });
        this.resetData();
        this.isDialogVisible = false;
        this.isDialogLoaded = false;
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.editFailureDescriptionFailed'),
        });
        this.resetData();
        this.isDialogVisible = false;
        this.isDialogLoaded = false;
      }
    },
    async addDictionary() {
      const payload = {
        dict_value: this.dialogInputs[0].value,
        description: this.dialogInputs[1].value,
      };
      this.isConfirmButtonLoading = true;
      try {
        await this.addDescription(payload);
        this.resetData();
        this.isDialogVisible = false;
        this.isDialogLoaded = false;
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.addFailureDescriptionFailed'),
        });
        this.resetData();
        this.isDialogVisible = false;
        this.isDialogLoaded = false;
      }
    },
    async handleDictionaryDelete() {
      this.isConfirmButtonLoading = true;
      try {
        await this.deleteDescription(this.editDictionary.id);
        this.resetData();
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.deleteFailureDescriptionFailed'),
        });
        this.resetData();
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
      }
    },

    openAddDialog() {
      this.isDialogVisible = true;
      this.isDialogLoaded = true;
    },
    openEditDialog(dictionary) {
      this.editDictionary = dictionary;
      this.isDialogVisible = true;
      this.isDialogLoaded = true;
    },
    openDeleteDialog(dictionary) {
      this.editDictionary = dictionary;
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    resetData() {
      this.isConfirmButtonLoading = false;
      this.editDictionary = undefined;
      this.dialogInputs[0].value = '';
      this.dialogInputs[1].value = '';
    },
    async init() {
      this.isFetching = true;
      await this.getDescriptions();
      this.isFetching = false;
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
